import { Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Link, Menu, MenuItem, Typography } from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import React, { ChangeEvent } from "react";
import WrappedButton from "../components/WrappedButton";
import { FormLine, getDocumentDisplayValue } from "./FormLine";

/**
 * Props for the DocumentUploadLine component
 */
interface DocUploadProps {
	onChange: Function;
	fl: FormLine;
}

/**
 * Component for handling document/file uploads for DOC, DOCFILE, and IMGFILE type codes.
 * IMGFILE shows an inline image preview; DOC/DOCFILE show a document link.
 */
export default function DocumentUploadLine(props: DocUploadProps) {
	const isImage = props.fl.typeCode === "IMGFILE";
	const [uploading, setUploading] = React.useState(false);
	const [fileName, setFileName] = React.useState<string | null>(null);
	const [error, setError] = React.useState<string | null>(null);
	const [menuAnchor, setMenuAnchor] = React.useState<null | HTMLElement>(null);
	const [selectMenuAnchor, setSelectMenuAnchor] = React.useState<null | HTMLElement>(null);
	const [showCamera, setShowCamera] = React.useState(false);
	const [cameraError, setCameraError] = React.useState<string | null>(null);
	const fileInputRef = React.useRef<HTMLInputElement>(null);
	const videoRef = React.useRef<HTMLVideoElement>(null);
	const streamRef = React.useRef<MediaStream | null>(null);

	const stopCamera = () => {
		streamRef.current?.getTracks().forEach((track) => track.stop());
		streamRef.current = null;
		if (videoRef.current) {
			videoRef.current.srcObject = null;
		}
	};

	const startCamera = async () => {
		setCameraError(null);
		try {
			const stream = await navigator.mediaDevices.getUserMedia({
				video: { facingMode: { ideal: "environment" } },
				audio: false,
			});
			streamRef.current = stream;
			if (videoRef.current) {
				videoRef.current.srcObject = stream;
				await videoRef.current.play();
			}
		} catch (err: any) {
			console.error("Camera access error:", err);
			setCameraError(err.message || "Unable to access camera");
		}
	};

	const openCamera = () => {
		setSelectMenuAnchor(null);
		setMenuAnchor(null);
		setShowCamera(true);
	};

	const openAttachMenu = (event: React.MouseEvent<HTMLElement>) => {
		if (!props.fl.isModifiable) return;
		setSelectMenuAnchor(event.currentTarget);
	};

	const attachFile = () => {
		setSelectMenuAnchor(null);
		handleMenuClose();
		triggerFileSelect();
	};

	React.useEffect(() => {
		if (showCamera) {
			startCamera();
		} else {
			stopCamera();
		}
		return () => stopCamera();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [showCamera]);

	const uploadFile = async (file: File) => {
		setFileName(file.name);
		setError(null);
		setUploading(true);

		try {
			const currentForm = (window as any).currentForm;
			const peopleId = currentForm?.parentValue || currentForm?.peopleId;

			if (!peopleId) {
				throw new Error("Unable to determine people_id for file upload");
			}

			const formData = new FormData();
			formData.append("file", file);
			formData.append("people_id", peopleId);
			formData.append("fileName", file.name);

			const response = await fetch("form/UploadDocument", {
				method: "POST",
				body: formData,
			});

			if (!response.ok) {
				throw new Error(`Upload failed: ${response.statusText}`);
			}

			const result = await response.json();

			props.fl.value = result.pictures_id;

			if (!props.fl.metadata) {
				props.fl.metadata = {};
			}
			props.fl.metadata.file_name = result.file_name;

			props.onChange();
		} catch (err: any) {
			console.error("File upload error:", err);
			setError(err.message || "Failed to upload file");
			setFileName(null);
		} finally {
			setUploading(false);
			if (fileInputRef.current) {
				fileInputRef.current.value = "";
			}
		}
	};

	/**
	 * Handles file selection from the input element.
	 * Uploads the file to the server via the staff session proxy.
	 * @param event - The change event from the file input
	 */
	const handleFileSelect = async (event: ChangeEvent<HTMLInputElement>) => {
		const files = event.target.files;
		if (!files || files.length === 0) return;
		await uploadFile(files[0]);
	};

	const capturePhoto = async () => {
		const video = videoRef.current;
		if (!video || !video.videoWidth) return;

		const canvas = document.createElement("canvas");
		canvas.width = video.videoWidth;
		canvas.height = video.videoHeight;
		canvas.getContext("2d")?.drawImage(video, 0, 0);

		const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.9));
		if (!blob) return;

		setShowCamera(false);
		const file = new File([blob], `photo_${Date.now()}.jpg`, { type: "image/jpeg" });
		await uploadFile(file);
	};

	/**
	 * Triggers the hidden file input click event
	 */
	const triggerFileSelect = () => {
		if (props.fl.isModifiable && fileInputRef.current) {
			fileInputRef.current.click();
		}
	};

	/**
	 * Opens the file in a new tab via the Picture/document endpoint
	 */
	const fileUrl = props.fl.value ? `Picture/document/${props.fl.value}` : null;
	const displayValue = getDocumentDisplayValue(props.fl);

	const openFile = () => {
		if (fileUrl) {
			window.open(fileUrl, "_blank");
		}
	};

	/**
	 * Handles click on the file link - shows menu if modifiable, opens file if not
	 */
	const handleLinkClick = (event: React.MouseEvent<HTMLElement>) => {
		if (!props.fl.value) return;
		
		if (props.fl.isModifiable) {
			// Show menu with options
			setMenuAnchor(event.currentTarget);
		} else {
			// Open file directly
			openFile();
		}
	};

	/**
	 * Closes the context menu
	 */
	const handleMenuClose = () => {
		setMenuAnchor(null);
	};

	/**
	 * Handles "Attach File" menu option
	 */
	const handleAttachFile = () => {
		attachFile();
	};

	/**
	 * Handles "Open File" menu option
	 */
	const handleOpenFile = () => {
		handleMenuClose();
		openFile();
	};

	return (
		<>
			{/* Hidden file input for file selection */}
			<input
				ref={fileInputRef}
				type="file"
				accept="*/*"
				style={{ display: "none" }}
				onChange={handleFileSelect}
				disabled={!props.fl.isModifiable || uploading}
			/>

			{/* Upload button */}
			{!props.fl.value && !uploading && (
				<Button
					variant="outlined"
					startIcon={<CloudUploadIcon />}
					onClick={openAttachMenu}
					disabled={!props.fl.isModifiable}
					sx={{ textTransform: "none" }}
				>
					{props.fl.isModifiable ? "Add Attachment" : "No Attachment"}
				</Button>
			)}

			<Menu anchorEl={selectMenuAnchor} open={Boolean(selectMenuAnchor)} onClose={() => setSelectMenuAnchor(null)}>
				<MenuItem onClick={openCamera}>Take Photo</MenuItem>
				<MenuItem onClick={handleAttachFile}>Attach File</MenuItem>
			</Menu>

			<Dialog fullWidth fullScreen open={showCamera} onClose={() => setShowCamera(false)} maxWidth="sm">
				<DialogTitle>Take Photo</DialogTitle>
				<DialogContent sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 1, flex: 1 }}>
					<video ref={videoRef} autoPlay playsInline muted style={{ width: "100%", maxHeight: "70vh", backgroundColor: "#000", borderRadius: 8, objectFit: "cover" }} />
					{cameraError && (
						<Typography variant="body2" color="error">
							{cameraError}
						</Typography>
					)}
				</DialogContent>
				<DialogActions>
					<WrappedButton onClick={() => setShowCamera(false)} size="small" caption="Cancel" />
					<WrappedButton onClick={capturePhoto} size="small" caption="Capture" isDisabled={!!cameraError} />
				</DialogActions>
			</Dialog>

			{/* Loading indicator during upload */}
			{uploading && (
				<div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
					<CircularProgress size={20} />
					<Typography variant="body2">Uploading {fileName}...</Typography>
				</div>
			)}

			{/* Display uploaded file info */}
			{props.fl.value && !uploading && isImage && (
				<div style={{ display: "flex", flexDirection: "column", gap: "8px", maxWidth: "100%" }}>
					<img
						src={fileUrl!}
						alt={displayValue || "Uploaded image"}
						onClick={handleLinkClick}
						style={{ maxWidth: "100%", maxHeight: 240, objectFit: "contain", cursor: props.fl.isModifiable ? "pointer" : "default" }}
					/>
					{displayValue && (
						<Typography variant="body2" color="text.secondary">{displayValue}</Typography>
					)}
				</div>
			)}

			{props.fl.value && !uploading && !isImage && (
				<div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
					<Link onClick={handleLinkClick} sx={{ cursor: "pointer" }}>
						{displayValue || "Document uploaded"}
					</Link>
				</div>
			)}

			{/* Context menu for existing attachments */}
			<Menu
				anchorEl={menuAnchor}
				open={Boolean(menuAnchor)}
				onClose={handleMenuClose}
			>
				<MenuItem onClick={openCamera}>Take Photo</MenuItem>
				<MenuItem onClick={handleAttachFile}>Attach File</MenuItem>
				<MenuItem onClick={handleOpenFile}>{isImage ? "Open Image" : "Open File"}</MenuItem>
			</Menu>

			{/* Error display */}
			{error && (
				<Typography variant="body2" color="error" sx={{ marginTop: 1 }}>
					{error}
				</Typography>
			)}
		</>
	);
}
